// Skyward PM C3 — loading icon (spec SWI_PM_C3_Loader_Spec_R1.1)
// Two designs, SVG + CSS animation only (styles in c3-loader.css):
//   bubble = drawing grid bubble, "C3" + small "SWI", orange arc running round it
//   levels = three floor slabs dropping into place, "SWI" stamped on the orange top slab
// "random" picks one of the two once per mount. Colours come from CSS variables, so
// the same icon works on the light app, the dark sign-in screen and on orange buttons.

import { useState } from 'react';

export type C3LoaderVariant = 'bubble' | 'levels' | 'random';
export type C3LoaderTone = 'auto' | 'dark' | 'on-accent';

export interface C3LoaderProps {
  /** Pixel size (square). 16–24 inline/buttons, 32–48 panels, 64–128 full page. Default 24. */
  size?: number;
  /** Default 'bubble'. 'random' = pick once per mount (use for full-page loads). */
  variant?: C3LoaderVariant;
  /** 'auto' = app theme, 'dark' = sign-in / dark surfaces, 'on-accent' = inside orange buttons. */
  tone?: C3LoaderTone;
  /** Screen-reader text. Default "Loading". */
  label?: string;
  className?: string;
}

/** Below these sizes detail is dropped so small icons stay clean. */
const TEXT_MIN = 24; // bubble shows "C3"
const MARK_MIN = 56; // both show "SWI"

function pick(v: C3LoaderVariant): 'bubble' | 'levels' {
  if (v !== 'random') return v;
  return Math.random() < 0.5 ? 'bubble' : 'levels';
}

function Bubble({ size }: { size: number }) {
  const mark = size >= MARK_MIN;
  return (
    <>
      <line className="c3l-stub" x1="24" y1="0" x2="24" y2="3.5" />
      <line className="c3l-stub" x1="24" y1="44.5" x2="24" y2="48" />
      <circle className="c3l-track" cx="24" cy="24" r="19" />
      <circle className="c3l-arc" cx="24" cy="24" r="19" />
      {size >= TEXT_MIN && (
        <text className="c3l-c3" x="24" y={mark ? 26.6 : 28.8} textAnchor="middle">C3</text>
      )}
      {mark && (
        <text className="c3l-swi" x="24.6" y="33.6" textAnchor="middle">SWI</text>
      )}
    </>
  );
}

function Slab({ y, n, mark }: { y: number; n: 1 | 2 | 3; mark?: boolean }) {
  return (
    <g className={`c3l-slab c3l-s${n}`}>
      <path className="c3l-l" d={`M8 ${y} L24 ${y + 8} L24 ${y + 11} L8 ${y + 3}Z`} />
      <path className="c3l-r" d={`M24 ${y + 8} L40 ${y} L40 ${y + 3} L24 ${y + 11}Z`} />
      <path className="c3l-t" d={`M8 ${y} L24 ${y - 8} L40 ${y} L24 ${y + 8}Z`} />
      {mark && (
        // matrix maps text onto the slab's top face (isometric plane)
        <text className="c3l-stamp" transform={`matrix(1 0.5 -1 0.5 24 ${y})`} x="0" y="1.9" textAnchor="middle">SWI</text>
      )}
    </g>
  );
}

function Levels({ size }: { size: number }) {
  return (
    <>
      <Slab y={33} n={1} />
      <Slab y={24} n={2} />
      <Slab y={15} n={3} mark={size >= MARK_MIN} />
    </>
  );
}

export function C3Loader({ size = 24, variant = 'bubble', tone = 'auto', label = 'Loading', className }: C3LoaderProps) {
  const [v] = useState(() => pick(variant));
  const cls = ['c3-loader', `c3-loader--${v}`, tone !== 'auto' ? `c3-loader--${tone}` : '', className ?? '']
    .filter(Boolean)
    .join(' ');
  return (
    <svg className={cls} viewBox="0 0 48 48" width={size} height={size} role="status" aria-label={label} data-variant={v}>
      <title>{label}</title>
      {v === 'bubble' ? <Bubble size={size} /> : <Levels size={size} />}
    </svg>
  );
}

/** Full-page / panel loading state: big icon (random design by default) + a line of text. */
export function C3PageLoading({ label = 'Loading', detail, variant = 'random', size = 72 }: {
  label?: string;
  detail?: string;
  variant?: C3LoaderVariant;
  size?: number;
}) {
  return (
    <div className="c3-page-loading">
      <C3Loader size={size} variant={variant} label={label} />
      <div>
        <strong>{label}</strong>
        {detail && <span>{detail}</span>}
      </div>
    </div>
  );
}

/** Button text while busy (V0.4.14): small bubble before the text; `accent` inside orange (primary) buttons. */
export function C3Busy({ text, accent = false }: { text: string; accent?: boolean }) {
  return (
    <span className="c3-busy">
      <C3Loader size={16} tone={accent ? 'on-accent' : 'auto'} label={text} />
      {text}
    </span>
  );
}
